// Stamps signer-entered values onto the real Manassas PDF templates using pdf-lib.
// Field coordinates come from documentTemplates.js (see that file for the
// top-down `yTop` coordinate convention).

import { PDFDocument, StandardFonts, rgb, PDFName } from 'pdf-lib';

const toPdfY = (pageHeight, yTop, nudge = 2) => pageHeight - yTop + nudge;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const formatDateValue = (value, format) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value + (typeof value === 'string' && value.length === 10 ? 'T00:00:00' : ''));
  if (Number.isNaN(d.getTime())) return String(value);
  if (format === 'dayOfMonthYear') {
    return `${ordinal(d.getDate())} day of ${MONTHS[d.getMonth()]}, 20 ${String(d.getFullYear()).slice(-2)}`;
  }
  if (format === 'monthDay') {
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// Used to render the day/month/year pieces of a split date field independently
// in the live overlay, matching exactly what fillTemplate stamps onto the PDF.
export const formatDateParts = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value + (typeof value === 'string' && value.length === 10 ? 'T00:00:00' : ''));
  if (Number.isNaN(d.getTime())) return null;
  return { day: ordinal(d.getDate()), month: MONTHS[d.getMonth()], year: String(d.getFullYear()).slice(-2) };
};

const fitFontSize = (font, text, baseSize, maxWidth) => {
  if (!maxWidth) return baseSize;
  let size = baseSize;
  while (size > 6 && font.widthOfTextAtSize(text, size) > maxWidth) size -= 0.5;
  return size;
};

const wrapText = (font, text, size, maxWidth) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = '';
  for (const word of words) {
    const attempt = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(attempt, size) > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = attempt;
    }
  }
  if (current) lines.push(current);
  return lines;
};

// WinAnsi (the standard PDF font encoding used here) only covers a small,
// fixed set of characters — emoji, most non-Latin scripts, invisible
// zero-width characters, and plenty of "smart" Unicode symbols all have no
// glyph in it at all. pdf-lib throws outright the moment it hits one rather
// than skipping it, crashing the whole PDF fill. Rather than trying to
// enumerate every codepoint that might cause this, test each character
// against the actual embedded font (the same call that fails inside
// drawText) and drop whatever it can't draw.
const sanitizeForFont = (font, str) => {
  let out = '';
  for (const ch of str) {
    try {
      font.widthOfTextAtSize(ch, 10);
      out += ch;
    } catch (_e) {
      // no glyph for this character — drop it rather than crash the fill
    }
  }
  return out;
};

const drawX = (page, x, yTop, pageHeight, font) => {
  page.drawText('X', {
    x: x + 0.5,
    y: toPdfY(pageHeight, yTop, 1.5),
    size: 9,
    font,
    color: rgb(0.1, 0.1, 0.1),
  });
};

// values: flat object keyed by field.key -> string | boolean | string[] | Date | dataURL
export async function fillTemplate(template, values) {
  const res = await fetch(template.file);
  if (!res.ok) throw new Error(`Failed to load template ${template.file}`);
  const bytes = await res.arrayBuffer();
  const pdfDoc = await PDFDocument.load(bytes);

  const pages = pdfDoc.getPages();

  // Some source PDFs (e.g. third-party payroll forms) are real interactive
  // AcroForms and can carry stray leftover values baked into a widget's own
  // appearance stream from whoever last touched the template. Annotations
  // (including form widgets) are composited by the viewer in a layer on top
  // of the page's content stream, so drawing over them isn't enough — the
  // widget's /AP appearance still paints on top afterward. We draw our own
  // overlay text at fixed coordinates instead of relying on the form at
  // all, so strip every widget's appearance/value outright.
  for (const page of pages) {
    let annots;
    try { annots = page.node.Annots(); } catch (_e) { annots = null; }
    if (!annots) continue;
    for (let i = 0; i < annots.size(); i++) {
      try {
        const annot = pdfDoc.context.lookup(annots.get(i));
        const subtype = annot?.get?.(PDFName.of('Subtype'));
        if (!subtype || subtype.toString() !== '/Widget') continue;
        annot.delete(PDFName.of('AP'));
        annot.delete(PDFName.of('V'));
        annot.delete(PDFName.of('DV'));
      } catch (_e) { /* not a widget annotation we can read */ }
    }
  }
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pageHeight = template.pageHeight;

  const imageCache = new Map();
  const embedSignature = async (dataUrl) => {
    if (imageCache.has(dataUrl)) return imageCache.get(dataUrl);
    const png = await pdfDoc.embedPng(dataUrl);
    imageCache.set(dataUrl, png);
    return png;
  };

  for (const field of template.fields) {
    const page = pages[field.page];
    const rawValue = values ? values[field.key] : undefined;
    // Only strings get drawn as text — leave booleans/arrays (checkboxes,
    // checkbox groups) and signature data URLs untouched.
    const value = typeof rawValue === 'string' ? sanitizeForFont(font, rawValue) : rawValue;

    if (field.type === 'signature') {
      if (!value) continue;
      const img = await embedSignature(value);
      const w = field.width;
      const h = (img.height / img.width) * w <= field.height ? (img.height / img.width) * w : field.height;
      page.drawImage(img, { x: field.x, y: toPdfY(pageHeight, field.yTop, 0), width: w, height: h });
      continue;
    }

    if (field.type === 'checkbox') {
      // Standalone checkboxes (e.g. "Not Applicable") aren't printed on the
      // original form, so draw the whole box + label ourselves rather than
      // just an X inside an existing glyph.
      if (field.standalone) {
        const y = toPdfY(pageHeight, field.yTop, 0);
        page.drawRectangle({ x: field.x, y, width: 9, height: 9, borderColor: rgb(0.1, 0.1, 0.1), borderWidth: 1 });
        if (value) {
          page.drawText('X', { x: field.x + 1, y: y + 1, size: 9, font, color: rgb(0.1, 0.1, 0.1) });
        }
        page.drawText(field.label, { x: field.x + 13, y: y + 1, size: 9, font, color: rgb(0.1, 0.1, 0.1) });
        continue;
      }
      if (value) drawX(page, field.x, field.yTop, pageHeight, font);
      continue;
    }

    if (field.type === 'radio') {
      const opt = field.options.find(o => o.value === value);
      if (opt) drawX(page, opt.x, opt.yTop, pageHeight, font);
      continue;
    }

    if (field.type === 'checkbox_group') {
      const selected = Array.isArray(value) ? value : [];
      for (const opt of field.options) {
        if (selected.includes(opt.value)) drawX(page, opt.x, opt.yTop, pageHeight, font);
      }
      continue;
    }

    if (value === undefined || value === null || value === '') continue;

    // "the ___ day of ______, 20__" style fields: the words "day", "of" and
    // "20" are already printed on the form, so each part gets drawn only in
    // its own blank rather than as one continuous string across all three.
    if (field.type === 'date' && field.daySlot) {
      const d = value instanceof Date ? value : new Date(value + (typeof value === 'string' && value.length === 10 ? 'T00:00:00' : ''));
      if (!Number.isNaN(d.getTime())) {
        const parts = [
          [ordinal(d.getDate()), field.daySlot],
          [MONTHS[d.getMonth()], field.monthSlot],
          [String(d.getFullYear()).slice(-2), field.yearSlot],
        ];
        for (const [text, slot] of parts) {
          const size = fitFontSize(font, text, field.size || 10, slot.maxWidth);
          page.drawText(text, { x: slot.x, y: toPdfY(pageHeight, slot.yTop, 2), size, font, color: rgb(0.1, 0.1, 0.1) });
        }
      }
      continue;
    }

    if (field.type === 'textarea') {
      const lines = wrapText(font, value, field.size, field.maxWidth).slice(0, field.lines || 3);
      lines.forEach((line, i) => {
        page.drawText(line, {
          x: field.x,
          y: toPdfY(pageHeight, field.yTop, 2) - i * (field.lineHeight || field.size + 2),
          size: field.size,
          font,
          color: rgb(0.1, 0.1, 0.1),
        });
      });
      continue;
    }

    let text = value;
    if (field.type === 'date') text = formatDateValue(value, field.format);
    if (field.type === 'currency' && text !== '') text = `${text}`;
    text = String(text);

    const size = fitFontSize(font, text, field.size || 10, field.maxWidth);
    page.drawText(text, {
      x: field.x,
      y: toPdfY(pageHeight, field.yTop, 2),
      size,
      font,
      color: rgb(0.1, 0.1, 0.1),
    });
  }

  return pdfDoc.save();
}

export const bytesToBlob = (bytes) => new Blob([bytes], { type: 'application/pdf' });
